import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Camera, Check, ChevronLeft, ChevronRight, Eraser, ImagePlus, PenLine, RotateCcw, Trash2 } from 'lucide-react';

type Tool = 'pen' | 'eraser';

interface IPoint {
  x: number;
  y: number;
  pressure: number;
}

interface IStroke {
  id: string;
  color: string;
  width: number;
  points: IPoint[];
}

interface IPhoto {
  id: string;
  src: string;
  x: number;
  y: number;
  width: number;
}

interface INoteData {
  strokes: IStroke[];
  photos: IPhoto[];
  memo: string;
}

const STORAGE_KEY = 'moashelf.note.v1';
const EMPTY_NOTE: INoteData = { strokes: [], photos: [], memo: '' };

const Wrapper = styled.main`
  --ink: #12384c;
  --line: #b8d2bc;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 132px;
  grid-template-rows: minmax(0, 1fr) 72px;
  width: 100vw;
  height: 100dvh;
  padding: max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right))
    max(12px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
  box-sizing: border-box;
  gap: 8px;
  background: #f8faf7;
  color: #17231a;
  overflow: hidden;

  @media (max-width: 720px), (orientation: portrait) and (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) auto;
  }
`;

const CanvasArea = styled.section`
  position: relative;
  min-width: 0;
  min-height: 0;
  border: 2px solid var(--line);
  background-color: #fff;
  background-image: linear-gradient(#edf3ed 1px, transparent 1px), linear-gradient(90deg, #edf3ed 1px, transparent 1px);
  background-size: 24px 24px;
  overflow: hidden;
  touch-action: none;
  user-select: none;
`;

const Drawing = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
  pointer-events: auto;
  cursor: crosshair;
  -webkit-user-select: none;
  user-select: none;
`;

const EmptyMessage = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  text-align: center;
  color: #728078;
  pointer-events: none;

  strong { color: #294b38; font-size: 20px; margin-bottom: 6px; }
  span { font-size: 14px; }
`;

const Memo = styled.textarea`
  position: absolute;
  left: 4%;
  bottom: 5%;
  width: min(52%, 520px);
  min-height: 110px;
  resize: both;
  border: 1px solid #b8d2bc;
  border-radius: 12px;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(255, 254, 236, 0.94);
  box-shadow: 0 8px 24px rgba(30, 70, 45, 0.12);
  font: inherit;
  line-height: 1.6;
  touch-action: manipulation;
`;

const Photo = styled.img<{ $selected: boolean; $width: number; $x: number; $y: number }>`
  position: absolute;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  width: ${(props) => props.$width}px;
  max-height: 60%;
  object-fit: contain;
  border: 3px solid ${(props) => (props.$selected ? '#2d5a2f' : '#fff')};
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  touch-action: none;
`;

const SideTools = styled.aside`
  grid-row: 1 / 3;
  grid-column: 2;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;

  @media (max-width: 720px), (orientation: portrait) and (max-width: 900px) {
    grid-row: 2;
    grid-column: 1;
    flex-direction: row;
    overflow-x: auto;
  }
`;

const BottomTools = styled.nav`
  display: flex;
  gap: 8px;
  min-width: 0;
  overflow-x: auto;
`;

const ToolButton = styled.button<{ $active?: boolean; $grow?: boolean }>`
  flex: ${(props) => (props.$grow ? '1 0 92px' : '0 0 64px')};
  min-height: 58px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: ${(props) => (props.$active ? 'var(--ink)' : '#fff')};
  color: ${(props) => (props.$active ? '#fff' : '#183a2a')};
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  svg { pointer-events: none; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const SaveState = styled.div`
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #45604f;
  font-size: 13px;
`;

const pathFromPoints = (points: IPoint[]) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y} l 0.1 0.1`;
  return points.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = points[index - 1];
    return `${path} Q ${previous.x} ${previous.y} ${(previous.x + point.x) / 2} ${(previous.y + point.y) / 2}`;
  }, '');
};

const createId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const NoteCanvas: React.FC = () => {
  const areaRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeStrokeRef = useRef<string | null>(null);
  const activePointerRef = useRef<number | null>(null);
  const dragRef = useRef<{ id: string; offsetX: number; offsetY: number } | null>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [note, setNote] = useState<INoteData>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '') as INoteData;
    } catch {
      return EMPTY_NOTE;
    }
  });
  const [history, setHistory] = useState<INoteData[]>([]);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [saved, setSaved] = useState(true);

  useEffect(() => {
    setSaved(false);
    const timeout = window.setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(note));
        setSaved(true);
      } catch {
        setSaved(false);
      }
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [note]);

  const snapshot = useCallback(() => {
    setHistory((current) => [...current.slice(-29), note]);
  }, [note]);

  const getPoint = (event: Pick<PointerEvent, 'clientX' | 'clientY' | 'pressure'>): IPoint => {
    const rect = areaRef.current!.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top, pressure: event.pressure || 0.5 };
  };

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    event.preventDefault();
    // 구형 iPadOS Safari에서는 SVG의 setPointerCapture가 없거나 예외를 던질 수 있다.
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // 포인터 ID 추적으로 계속 필기한다.
    }
    activePointerRef.current = event.pointerId;
    const point = getPoint(event.nativeEvent);

    if (tool === 'eraser') {
      const target = event.target as SVGPathElement;
      const strokeId = target.dataset.strokeId;
      if (strokeId) {
        snapshot();
        setNote((current) => ({ ...current, strokes: current.strokes.filter((stroke) => stroke.id !== strokeId) }));
      }
      return;
    }

    snapshot();
    const id = createId();
    activeStrokeRef.current = id;
    setSelectedPhotoId(null);
    setNote((current) => ({
      ...current,
      strokes: [...current.strokes, { id, color: '#12384c', width: Math.max(2, 3.5 * point.pressure), points: [point] }],
    }));
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!activeStrokeRef.current || activePointerRef.current !== event.pointerId) return;
    event.preventDefault();
    const nativeEvent = event.nativeEvent;
    const events = nativeEvent.getCoalescedEvents?.() ?? [nativeEvent];
    const points = events.map(getPoint);
    setNote((current) => ({
      ...current,
      strokes: current.strokes.map((stroke) =>
        stroke.id === activeStrokeRef.current ? { ...stroke, points: [...stroke.points, ...points] } : stroke,
      ),
    }));
  };

  const stopDrawing = (event: React.PointerEvent<SVGSVGElement>) => {
    if (activePointerRef.current !== event.pointerId) return;
    activeStrokeRef.current = null;
    activePointerRef.current = null;
  };

  const handlePhotoInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      snapshot();
      const photo: IPhoto = { id: createId(), src: String(reader.result), x: 36, y: 36, width: 240 };
      setNote((current) => ({ ...current, photos: [...current.photos, photo] }));
      setSelectedPhotoId(photo.id);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handlePhotoPointerDown = (event: React.PointerEvent<HTMLImageElement>, photo: IPhoto) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    snapshot();
    setSelectedPhotoId(photo.id);
    dragRef.current = { id: photo.id, offsetX: event.clientX - photo.x, offsetY: event.clientY - photo.y };
  };

  const handlePhotoPointerMove = (event: React.PointerEvent<HTMLImageElement>) => {
    if (!dragRef.current || !areaRef.current) return;
    const rect = areaRef.current.getBoundingClientRect();
    const x = Math.max(0, event.clientX - rect.left - (dragRef.current.offsetX - rect.left));
    const y = Math.max(0, event.clientY - rect.top - (dragRef.current.offsetY - rect.top));
    setNote((current) => ({
      ...current,
      photos: current.photos.map((photo) => (photo.id === dragRef.current?.id ? { ...photo, x, y } : photo)),
    }));
  };

  const handleUndo = () => {
    const previous = history[history.length - 1];
    if (!previous) return;
    setNote(previous);
    setHistory((current) => current.slice(0, -1));
  };

  const handleDelete = () => {
    snapshot();
    if (selectedPhotoId) {
      setNote((current) => ({ ...current, photos: current.photos.filter((photo) => photo.id !== selectedPhotoId) }));
      setSelectedPhotoId(null);
    } else {
      setNote(EMPTY_NOTE);
    }
  };

  const StrokeList = note.strokes.map((stroke) => (
    <path
      key={stroke.id}
      data-stroke-id={stroke.id}
      d={pathFromPoints(stroke.points)}
      fill='none'
      stroke={stroke.color}
      strokeWidth={stroke.width}
      strokeLinecap='round'
      strokeLinejoin='round'
      pointerEvents={tool === 'eraser' ? 'stroke' : 'none'}
    />
  ));

  const PhotoList = note.photos.map((photo) => (
    <Photo
      key={photo.id}
      src={photo.src}
      alt='추가한 자료'
      draggable={false}
      $selected={selectedPhotoId === photo.id}
      $width={photo.width}
      $x={photo.x}
      $y={photo.y}
      onPointerDown={(event) => handlePhotoPointerDown(event, photo)}
      onPointerMove={handlePhotoPointerMove}
      onPointerUp={() => { dragRef.current = null; }}
    />
  ));

  return (
    <Wrapper>
      <CanvasArea ref={areaRef} onPointerDown={() => setSelectedPhotoId(null)}>
        {note.strokes.length === 0 && note.photos.length === 0 && !note.memo && (
          <EmptyMessage><strong>펜으로 바로 시작하세요</strong><span>필기와 사진은 이 기기에 자동 저장됩니다.</span></EmptyMessage>
        )}
        <Drawing
          aria-label='필기 영역'
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onLostPointerCapture={stopDrawing}>
          {StrokeList}
        </Drawing>
        {PhotoList}
        <Memo
          aria-label='메모'
          placeholder='메모를 입력하세요'
          value={note.memo}
          onFocus={snapshot}
          onChange={(event) => setNote((current) => ({ ...current, memo: event.target.value }))}
        />
      </CanvasArea>

      <SideTools aria-label='필기 도구'>
        <ToolButton $active={tool === 'pen'} $grow onClick={() => setTool('pen')}><PenLine size={22} />펜</ToolButton>
        <ToolButton $active={tool === 'eraser'} $grow onClick={() => setTool('eraser')}><Eraser size={22} />지우개</ToolButton>
        <ToolButton $grow onClick={() => inputRef.current?.click()}><ImagePlus size={22} />사진</ToolButton>
        <ToolButton $grow onClick={handleUndo} disabled={history.length === 0}><RotateCcw size={22} />되돌리기</ToolButton>
        <ToolButton $grow onClick={handleDelete}><Trash2 size={22} />{selectedPhotoId ? '사진 삭제' : '전체 삭제'}</ToolButton>
        <SaveState><Check size={16} />{saved ? '자동 저장됨' : '저장 중'}</SaveState>
      </SideTools>

      <BottomTools aria-label='빠른 도구'>
        <ToolButton aria-label='이전 페이지' disabled><ChevronLeft size={22} /></ToolButton>
        <ToolButton $grow onClick={() => setTool('pen')} $active={tool === 'pen'}>필기</ToolButton>
        <ToolButton $grow onClick={() => inputRef.current?.click()}><Camera size={22} />촬영/사진</ToolButton>
        <ToolButton $grow onClick={() => document.querySelector<HTMLTextAreaElement>('[aria-label="메모"]')?.focus()}>메모</ToolButton>
        <ToolButton aria-label='다음 페이지' disabled><ChevronRight size={22} /></ToolButton>
      </BottomTools>

      <input ref={inputRef} hidden type='file' accept='image/*' capture='environment' onChange={handlePhotoInput} />
    </Wrapper>
  );
};

export default NoteCanvas;
